import { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import { DatabaseIcon, UserIcon } from '@hugeicons-pro/core-bulk-rounded'
import { Tick01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { Avatar, Box, Button, Container, CssBaseline, LinearProgress, Stack, Typography } from '@mui/material'
import { styled, ThemeProvider } from '@mui/material/styles'
import PropTypes from 'prop-types'

import AppLogo from '@/common/components/AppLogo/AppLogo'
import { useToolBoxTreatments } from '@/common/hooks/useToolBoxTreatments'
import { theme as lightTheme } from '@/common/themes/LightTheme'
import { StrikerThemeOverride } from '@/common/themes/overrides/StrikerThemeOverride'
import { WhitelabelThemeOverride } from '@/common/themes/overrides/WhitelabelThemeOverride'
import { LOADING_STEPS } from '@/common/utils/Constants/Constants'
import { getErrorAction, shouldShowAction } from '@/common/utils/ErrorActionMapping'

const TechnicalBackground = styled(Box)(({ theme }) => ({
    background: theme.palette.background.default,
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    position: 'relative',
}))

const TechnicalContainer = styled(Container)(({ theme }) => ({
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: '8px',
    padding: theme.spacing(4),

    '&::after': {
        content: '"DRAWING NO: TT-LOAD-001 | SCALE: 1:1 | REV: A"',
        position: 'absolute',
        bottom: -12,
        right: 24,
        fontSize: '12px',
        color: theme.palette.text.secondary,
        background: theme.palette.background.paper,
        padding: '2px 8px',
        border: `1px solid ${theme.palette.action.disabled}`,
        borderRadius: '4px',
    },
}))

const TechnicalProgressContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.active}`,
    padding: theme.spacing(3),
    borderRadius: '8px',
}))

const TechnicalTypography = styled(Typography)({
    letterSpacing: '0.5px',
})

const TechnicalSkeleton = styled(LinearProgress)(({ theme }) => ({
    height: 8,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: theme.palette.action.hover,
    borderRadius: 4,
}))

// Static step definitions for actual loading steps (tracked in LoadingContext)
// 3 steps: Auth, User Context, Org. User role and manufacturing data load in background (not tracked)
const LOADING_STEP_DEFS = [
    {
        key: [LOADING_STEPS.AUTHENTICATION],
        icon: UserIcon,
        label: 'Authenticating user',
        code: 'AUTH-001',
    },
    {
        key: [LOADING_STEPS.USER_CONTEXT],
        icon: UserIcon,
        label: 'Loading user context',
        code: 'USER-002',
    },
    {
        key: [LOADING_STEPS.ORGANISATION],
        icon: DatabaseIcon,
        label: 'Loading organisation data',
        code: 'ORG-003',
    },
]

// Visual-only step shown when all loading completes (not tracked in LoadingContext)
const COMPLETE_STEP = {
    icon: Tick01Icon,
    label: 'Initialisation complete',
    code: 'LOAD-COMPLETE-004',
}

const TbxLoadingPageComponent = ({ currentStep = 0, isComplete = false, onErrorAction = null, steps = null }) => {
    const { t } = useTranslation()
    const { showLibellulaWhitelabelling, showStrikerWhitelabelling } = useToolBoxTreatments()
    const [isActionLoading, setIsActionLoading] = useState(false)

    // Optimize theme selection with better memoization
    const theme = useMemo(
        () =>
            showLibellulaWhitelabelling
                ? WhitelabelThemeOverride(lightTheme)
                : showStrikerWhitelabelling
                  ? StrikerThemeOverride(lightTheme)
                  : lightTheme,
        [showLibellulaWhitelabelling, showStrikerWhitelabelling]
    )

    // Determine current active step and error information
    let activeStep = null
    let hasError = false
    let errorMessage = null
    let errorObject = null
    let errorLoadingStep = null

    if (steps) {
        // Find the current active step based on real steps
        for (const step of LOADING_STEP_DEFS) {
            const keys = Array.isArray(step.key) ? step.key : [step.key]
            const realStates = keys.map((k) => steps[k]).filter(Boolean)

            const anyLoading = realStates.some((s) => s?.isLoading)
            const anyError = realStates.some((s) => !!s?.error)

            if (anyLoading) {
                activeStep = step
                break
            }
            if (anyError) {
                activeStep = step
                hasError = true
                const errorStep = realStates.find((s) => !!s?.error)
                if (errorStep) {
                    errorMessage = errorStep?.errorMessage
                    errorObject = errorStep?.errorObject
                    // Find the loading step key for the error
                    const errorStepKey = keys.find((k) => steps[k]?.error)
                    errorLoadingStep = errorStepKey
                }
                break
            }
        }
    } else {
        // Use artificial progress mode
        activeStep = LOADING_STEP_DEFS[currentStep] || LOADING_STEP_DEFS[0]
    }

    // Fallback to first step if no active step found
    if (!activeStep && !isComplete) {
        activeStep = LOADING_STEP_DEFS[0]
    }

    if (isComplete) {
        activeStep = COMPLETE_STEP
    }

    // Determine error action configuration
    const errorActionConfig = useMemo(() => {
        if (!hasError || !errorObject) return null

        const action = getErrorAction(errorObject)
        const showAction = shouldShowAction(errorObject)

        return showAction ? action : null
    }, [hasError, errorObject])

    // Handle error action execution
    const handleErrorAction = async () => {
        if (!errorActionConfig || !onErrorAction || isActionLoading) return

        try {
            setIsActionLoading(true)
            console.debug('[TbxLoadingPage] Executing error action:', errorActionConfig.actionType)

            await onErrorAction(errorActionConfig.actionType, errorLoadingStep, errorObject)

            console.debug('[TbxLoadingPage] Error action completed successfully')
        } catch (error) {
            console.error('[TbxLoadingPage] Error action failed:', error)
        } finally {
            setIsActionLoading(false)
        }
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <TechnicalBackground>
                <TechnicalContainer maxWidth="md">
                    <Stack spacing={4}>
                        <AppLogo />
                        {/* Technical Progress Section */}
                        <TechnicalProgressContainer
                            aria-atomic="true"
                            aria-busy={!hasError ? !isComplete : null}
                            aria-label={t('System initialization')}
                            aria-live="polite"
                            role="status"
                        >
                            <Stack
                                alignItems="center"
                                direction="row"
                                justifyContent="center"
                                mb={2}
                            >
                                <TechnicalTypography
                                    color={theme.palette.text.secondary}
                                    variant="strong2"
                                >
                                    {t('SYSTEM INITIALISATION')}
                                </TechnicalTypography>
                            </Stack>

                            {/* Current Step Information */}
                            <Stack
                                alignItems="center"
                                aria-label={
                                    hasError
                                        ? `Error occurred: ${errorMessage}`
                                        : `Current step: ${t(activeStep.label)}`
                                }
                                direction="row"
                                mt={3}
                                role="status"
                                spacing={2}
                            >
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        backgroundColor:
                                            theme.palette.mode === 'dark'
                                                ? theme.palette.action.hover
                                                : theme.palette.action.selected,
                                        border: `1px solid ${hasError ? theme.palette.error.main : theme.palette.secondary.main}`,
                                    }}
                                >
                                    <HugeiconsIcon
                                        color={hasError ? theme.palette.error.main : theme.palette.secondary.main}
                                        icon={activeStep.icon}
                                        size={18}
                                    />
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Stack
                                        alignItems="center"
                                        direction="row"
                                        justifyContent="space-between"
                                    >
                                        <TechnicalTypography
                                            color={hasError ? theme.palette.error.main : theme.palette.text.secondary}
                                            sx={{ fontWeight: 600 }}
                                            variant="small"
                                        >
                                            {t(activeStep.label).toUpperCase()}
                                        </TechnicalTypography>
                                        <TechnicalTypography
                                            color={theme.palette.text.secondary}
                                            variant="caption"
                                        >
                                            {activeStep.code}
                                        </TechnicalTypography>
                                    </Stack>
                                    {hasError ? (
                                        <Stack
                                            aria-live="assertive"
                                            role="alert"
                                            spacing={1}
                                        >
                                            <TechnicalTypography
                                                aria-label={`Error: ${errorMessage}`}
                                                color={theme.palette.error.main}
                                                sx={{ marginTop: 0.5 }}
                                                variant="caption"
                                            >
                                                ✗ {t(errorMessage?.toUpperCase())}
                                            </TechnicalTypography>
                                        </Stack>
                                    ) : (
                                        <Box mt={0.5}>
                                            <TechnicalSkeleton
                                                color="primary"
                                                variant={'indeterminate'}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Stack>
                            <Stack>
                                {errorActionConfig ? (
                                    <Box
                                        align="right"
                                        sx={{ mt: 1 }}
                                    >
                                        <Button
                                            aria-describedby={
                                                errorActionConfig.description ? 'error-action-description' : undefined
                                            }
                                            aria-label={`${isActionLoading ? t('PROCESSING...') : errorActionConfig.buttonText}. ${errorActionConfig.description || ''}`}
                                            color={errorActionConfig.color}
                                            disabled={isActionLoading}
                                            size="small"
                                            type="button"
                                            variant={errorActionConfig.variant}
                                            onClick={handleErrorAction}
                                        >
                                            {isActionLoading
                                                ? t('PROCESSING...')
                                                : t(errorActionConfig.buttonText.toUpperCase())}
                                        </Button>
                                    </Box>
                                ) : null}
                            </Stack>
                        </TechnicalProgressContainer>
                    </Stack>
                </TechnicalContainer>
            </TechnicalBackground>
        </ThemeProvider>
    )
}

/**
 * Safe comparison function for React.memo to prevent unnecessary re-renders
 * Compares only primitive values to avoid object reference issues
 *
 * @param {Object} prevProps - Previous component props
 * @param {Object} nextProps - Next component props
 * @returns {boolean} Whether props are equal
 */
const areEqual = (prevProps, nextProps) => {
    // Compare primitive props directly
    if (prevProps.currentStep !== nextProps.currentStep) return false
    if (prevProps.isComplete !== nextProps.isComplete) return false

    // Compare function references - if onErrorAction changes, we need to re-render
    if (prevProps.onErrorAction !== nextProps.onErrorAction) return false

    // For steps object, compare relevant primitive indicators instead of deep comparison
    const prevStepsCount = prevProps.steps ? Object.keys(prevProps.steps).length : 0
    const nextStepsCount = nextProps.steps ? Object.keys(nextProps.steps).length : 0
    if (prevStepsCount !== nextStepsCount) return false

    // Compare loading/error states without deep object comparison
    if (prevProps.steps && nextProps.steps) {
        const prevSteps = prevProps.steps
        const nextSteps = nextProps.steps

        // Check if any step loading status changed
        for (const key of Object.keys(prevSteps)) {
            const prevStep = prevSteps[key]
            const nextStep = nextSteps[key]

            if (!nextStep) return false
            if (prevStep.isLoading !== nextStep.isLoading) return false
            if (prevStep.isSuccess !== nextStep.isSuccess) return false
            if (!!prevStep.error !== !!nextStep.error) return false // Compare error existence only
            // Compare error object existence for action buttons
            if (!!prevStep.errorObject !== !!nextStep.errorObject) return false
        }
    }

    return true
}

// Export memoized component with safe comparison
export const TbxLoadingPage = memo(TbxLoadingPageComponent, areEqual)

TbxLoadingPageComponent.propTypes = {
    currentStep: PropTypes.number,
    isComplete: PropTypes.bool,
    steps: PropTypes.object,
    onErrorAction: PropTypes.func,
}
